import { formatLocalDate } from '../../utils/localDateTime';
import React from 'react';
import { TravelSecurityBriefResult, Page, NavParams } from '@types';
import { ICONS } from '../constants';
import UnifiedMap from '../UnifiedMap';
import useGoogleMaps from '../useGoogleMaps';
import { Section, md } from './ReportShared';
import RecommendedSuitesDisplay from './RecommendedSuitesDisplay';
import { COUNTRY_CODES } from '../../common/data';
import { normalizeISO3 } from '../../utils/geography';

interface TravelSecurityBriefDisplayProps {
    result: TravelSecurityBriefResult;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
}

const TravelSecurityBriefDisplay: React.FC<TravelSecurityBriefDisplayProps> = ({ result, onNavigate }) => {
    const { isLoaded } = useGoogleMaps();
    const destinationCountry = result.destinationCountry?.split(',').map(part => part.trim()).filter(Boolean).at(-1) || '';
    const destinationIso3 = (COUNTRY_CODES as any)[destinationCountry]?.iso3 || normalizeISO3(destinationCountry);
    const rawMapMarkers = [
        ...(result.mapMarkers || []),
        ...(result.medicalFacilities || []).map(facility => ({
            ...facility,
            type: 'Hospital',
            description: facility.quality
        })),
        ...(result.embassies || []).map(embassy => ({
            ...embassy,
            name: `${embassy.country} Embassy`,
            type: 'Government',
            description: `${embassy.address} ${embassy.contact}`.trim()
        })),
        ...(result.governmentInfrastructure || []).map(infra => ({
            ...infra,
            lat: Number(infra.lat),
            lng: Number(infra.lng)
        })),
        ...(result.riskEvents || []).map(evt => ({
            ...evt,
            isAlert: true,
            title: evt.title
        }))
    ];
    const mapMarkers = rawMapMarkers
        .map((marker: any) => ({ ...marker, lat: Number(marker.lat), lng: Number(marker.lng) }))
        .filter((marker: any) => Number.isFinite(marker.lat)
            && Number.isFinite(marker.lng)
            && (marker.lat !== 0 || marker.lng !== 0));
    const localThreatAlerts = [
        ...(result.riskEvents || []).map((event: any, index: number) => ({
            ...event,
            id: event.id || `travel-risk-${index}`,
            iso3: destinationIso3,
            type: event.type || 'threat',
            summary: event.summary || event.description
        })),
        ...(result.keyThreats || []).map((threat, index) => ({
            ...threat,
            id: `travel-threat-${index}`,
            iso3: destinationIso3,
            lat: mapMarkers[0]?.lat,
            lng: mapMarkers[0]?.lng,
            title: threat.risk || threat.threat,
            summary: threat.description || threat.mitigation,
            type: 'threat',
            riskLevel: (threat as any).riskLevel || (threat as any).level || result.threatLevel,
            riskJustification: (threat as any).riskJustification || 'Saved destination threat level; no separate numerical item estimate was supplied.'
        }))
    ];

    const heatmapPoints = (result.threatGroupHeatmap || []).map(point => ({
        lat: Number(point.lat),
        lng: Number(point.lng),
        weight: Number(point.weight || 5)
    }));

    const tacticalRoutes = result.routePlanning ? [
        {
            name: result.routePlanning?.primarySecureRoute?.name || 'Primary Secure Route',
            origin: result.routePlanning?.primarySecureRoute?.origin || '',
            destination: result.routePlanning?.primarySecureRoute?.destination || '',
            waypoints: result.routePlanning?.primarySecureRoute?.waypoints || [],
            color: '#10b981' // Emerald
        },
        ...(result.routePlanning.alternateHotelRoutes || []).map(route => ({
            name: `Hotel: ${route.hotelName}`,
            origin: route.origin || '',
            destination: route.destination || '',
            waypoints: route.waypoints || [],
            color: '#3b82f6', // Blue
            isDash: true
        })),
        ...(result.routePlanning.detourAnalysis || []).map(route => ({
            name: `Detour: ${route.scenario}`,
            origin: route.origin || '',
            destination: route.destination || '',
            waypoints: route.waypoints || [],
            color: '#f59e0b', // Amber
            isDash: true
        }))
    ] : [];

    const mapCenter = tacticalRoutes[0]?.waypoints[0] || mapMarkers[0] || { lat: 20, lng: 0 };

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-12"> 
            {/* Automated Analysis Disclaimer */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start space-x-3 mb-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div className="flex-grow">
                    <p className="text-sm text-blue-800 font-bold">Project Intelligence Sync Active</p>
                    <p className="text-sm text-blue-700">
                        The background daily analysis will automatically update the active SRM project risk profile it is associated with.
                    </p>
                </div>
            </div>

            {/* --- PRIMARY BRANDING & IDENTIFICATION --- */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b-2 border-slate-900 pb-4 mb-6">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="bg-slate-900 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-tighter uppercase">IntellOptics ECC</span>
                        <span className="text-slate-500 font-bold text-[10px] tracking-widest uppercase">System: 0x77-TRAVEL</span>
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none uppercase">
                        Travel Security Brief: {result.destinationCountry}
                    </h2>
                    <div className="text-sm text-slate-500 font-medium mt-1 uppercase tracking-wider">
                        Strategic Mobility & Personnel Protection
                    </div>
                </div>
                <div className="flex flex-col md:items-end text-right">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Document Registry</div>
                    <div className="text-sm font-bold text-slate-700">{formatLocalDate(new Date())}</div>
                    <div className="text-[10px] text-slate-400 mt-1 uppercase">
                        Threat Level: <span className="text-red-600 font-black tracking-tighter">{result.threatLevel}</span>
                    </div>
                </div>
            </div>

            <Section title="Executive Summary">
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                    <div className="prose prose-sm text-slate-700 max-w-none leading-relaxed" dangerouslySetInnerHTML={{ __html: md.render(result.executiveSummary || 'Executive summary unavailable.') }} />
                    <div className="mt-4 text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Advisory Level: {result.advisoryLevel || 'Review official guidance'}
                    </div>
                </div>
            </Section>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* --- SIDEBAR: CRITICAL INTELLIGENCE --- */}
                <div className="lg:col-span-1 space-y-6 order-2 lg:order-1">
                    <div className="bg-white border-2 border-slate-200 rounded-xl p-4 shadow-sm">
                        <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest border-b border-slate-100 pb-2 mb-4">
                            Operational Context
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-[9px] font-bold text-slate-400 uppercase">Emergency Contact</label>
                                <p className="text-sm font-black text-blue-600">{result.logistics?.emergencyContact || "112 / 911"}</p>
                            </div>
                            <div>
                                <label className="text-[9px] font-bold text-slate-400 uppercase">Entry Requirements</label>
                                <p className="text-xs text-slate-600 leading-relaxed font-medium">{result.logistics?.visaRequirements}</p>
                            </div>
                            <div>
                                <label className="text-[9px] font-bold text-slate-400 uppercase">Medical Profile</label>
                                <p className="text-xs text-slate-600 leading-relaxed font-medium">{result.logistics?.medicalPrecautions}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-slate-900 text-white rounded-xl p-5 shadow-xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-4 text-slate-800 opacity-30 group-hover:scale-110 transition-transform">{ICONS.shield}</div>
                        <h3 className="text-[10px] font-black uppercase tracking-widest text-blue-400 mb-4 border-b border-white/10 pb-2">
                           Grounding Logic
                        </h3>
                        <p className="text-[11px] text-slate-300 leading-relaxed italic font-medium">
                            Analysis grounded in current geopolitical volatility and embassy-level security bulletins for {result.destinationCountry}.
                        </p>
                    </div>
                </div>

                {/* --- MAIN CONTENT: STRATEGIC MOBILITY --- */}
                <div className="lg:col-span-3 space-y-8 order-1 lg:order-2">
                    <Section title="Tactical Mobility & Route Planning">
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-slate-50 transition-all">
                            <div className="w-full h-[500px] relative">
                                <UnifiedMap
                                    isLoaded={isLoaded}
                                    initialViewState={{ 
                                        longitude: mapCenter?.lng || 0, 
                                        latitude: mapCenter?.lat || 0, 
                                        zoom: 12 
                                    }}
                                    theme="light"
                                    markers={mapMarkers}
                                    alerts={localThreatAlerts}
                                    heatmapPoints={heatmapPoints}
                                    tacticalRoutes={tacticalRoutes}
                                    filterIso3={destinationIso3}
                                />
                                <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur shadow-xl border border-slate-200 p-3 rounded-lg max-w-xs">
                                    <h5 className="text-[10px] font-black uppercase text-slate-400 mb-2">Route Legend</h5>
                                    <div className="space-y-1.5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-0.5 bg-emerald-500"></div>
                                            <span className="text-[9px] font-bold text-slate-700">PRIMARY SECURE CORRIDOR</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-0.5 bg-blue-500 border-t border-dashed border-white"></div>
                                            <span className="text-[9px] font-bold text-slate-700">HOTEL TRANSIT OPTION</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-0.5 bg-amber-500 border-t border-dashed border-white"></div>
                                            <span className="text-[9px] font-bold text-slate-700">DETOUR / SCENARIO VARIANT</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 bg-slate-50 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h6 className="text-[10px] font-black text-slate-400 uppercase mb-2">Primary Route Directive</h6>
                                    <p className="text-xs text-slate-600 leading-relaxed font-medium">
                                        {result.routePlanning?.primarySecureRoute?.description || 'Follow standard secure transit protocols for this sector.'}
                                    </p>
                                </div>
                                <div className="space-y-3">
                                    <h6 className="text-[10px] font-black text-slate-400 uppercase mb-2">Sector Vulnerabilities</h6>
                                    <div className="flex flex-wrap gap-2">
                                        {(result.logistics?.dangerousAreas || []).map((area, i) => (
                                            <span key={i} className="text-[9px] font-bold bg-red-100 text-red-700 px-2.5 py-1 rounded-full border border-red-200">
                                                {area}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Section>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Section title="Vulnerability Profile">
                            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                                <ul className="space-y-4">
                                    {(result.travelRisks || []).map((risk, i) => (
                                        <li key={i} className="flex items-start gap-4 pb-4 border-b border-slate-50 last:border-0 last:pb-0 group">
                                            <div className="p-2 bg-slate-50 text-slate-400 group-hover:bg-red-50 group-hover:text-red-500 transition-colors rounded-lg">
                                                {ICONS.alert}
                                            </div>
                                            <div>
                                                <h6 className="text-sm font-black text-slate-800 uppercase tracking-tight">{risk.risk}</h6>
                                                <p className="text-xs text-slate-500 leading-relaxed font-medium mt-0.5">{risk.description}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Section>

                        <Section title="Risk Mitigation Control">
                            <div className="bg-indigo-950 text-white rounded-xl shadow-2xl relative overflow-hidden h-full">
                                <div className="absolute -right-16 -bottom-16 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
                                <div className="p-8 relative z-10 flex flex-col h-full">
                                    <h6 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.3em] mb-6 flex items-center gap-2">
                                        <span className="w-1.5 h-6 bg-indigo-500 rounded-full"></span>
                                        Operational Protocols
                                    </h6>
                                    <ul className="space-y-4 flex-grow">
                                        {(result.mitigationMeasures || []).map((measure, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <span className="p-1.5 bg-indigo-500/20 text-indigo-300 rounded-lg">{ICONS.shield}</span>
                                                <span className="text-xs text-indigo-100/90 leading-relaxed font-medium mt-0.5">{measure}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-8 pt-6 border-t border-white/10">
                                        <div className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-1.5">Sector Review Status</div>
                                        <div className="flex items-center gap-2">
                                            <div className="h-1 flex-grow bg-white/10 rounded-full overflow-hidden">
                                                <div className="h-full bg-indigo-500" style={{ width: '92%' }}></div>
                                            </div>
                                            <span className="text-[9px] font-mono text-indigo-300 font-bold">92%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Section>
                    </div>

                    {/* --- LOCAL INTELLIGENCE & NEWS FEED --- */}
                    {result.localNewsFeed && (
                        <Section title="Local Intelligence & News Feed">
                            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-8">
                                <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl">
                                    <h6 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Media Landscape Summary</h6>
                                    <p className="text-xs text-slate-700 leading-relaxed font-medium">{result.localNewsFeed.summary}</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                                    <div>
                                        <h6 className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-4 flex items-center gap-2">
                                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                                            Top Stories: Homeland Security & Unrest
                                        </h6>
                                        <div className="space-y-4">
                                            {(result.localNewsFeed?.topStories || []).map((story, i) => (
                                                <div key={i} className="group cursor-default border-l-2 border-slate-100 hover:border-blue-500 pl-4 transition-colors">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase ${
                                                            story.category === 'Homeland Security' ? 'bg-indigo-100 text-indigo-700' :
                                                            story.category === 'Crime' ? 'bg-red-100 text-red-700' :
                                                            story.category === 'Civil Unrest' ? 'bg-orange-100 text-orange-700' :
                                                            'bg-slate-100 text-slate-700'
                                                        }`}>
                                                            {story.category}
                                                        </span>
                                                        <span className="text-[9px] font-bold text-slate-400">{story.source}</span>
                                                    </div>
                                                    <h5 className="text-sm font-black text-slate-800 leading-tight mb-1 group-hover:text-blue-600 transition-colors">{story.title}</h5>
                                                    <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2">{story.summary}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-8">
                                        <div>
                                            <h6 className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-4">Localized Media Channels</h6>
                                            <div className="grid grid-cols-1 gap-3">
                                                {(result.localNewsFeed?.tvChannels || []).map((channel, i) => (
                                                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl hover:bg-white hover:shadow-md transition-all group">
                                                        <div>
                                                            <div className="text-xs font-black text-slate-900">{channel.name}</div>
                                                            <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{channel.description}</div>
                                                        </div>
                                                        {channel.url && (
                                                            <a href={channel.url} target="_blank" rel="noopener noreferrer" className="p-2 bg-white text-slate-400 hover:text-blue-600 rounded-lg shadow-sm border border-slate-200 transition-colors">
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                                                                </svg>
                                                            </a>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <h6 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Newspapers</h6>
                                                <div className="space-y-1.5">
                                                    {(result.localNewsFeed?.newspapers || []).map((paper, i) => (
                                                        <a key={i} href={paper.url} target="_blank" rel="noopener noreferrer" className="block text-[11px] font-bold text-slate-600 hover:text-blue-600 transition-colors truncate">
                                                            {paper.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                            <div>
                                                <h6 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Live Video Feeds</h6>
                                                <div className="space-y-1.5">
                                                    {(result.localNewsFeed?.liveFeeds || []).map((feed, i) => (
                                                        <a key={i} href={feed.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 hover:text-red-600 transition-colors">
                                                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                                                            {feed.name}
                                                        </a>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Section>
                    )}

                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 relative overflow-hidden shadow-inner ring-1 ring-black/5">
                        <div className="flex items-center justify-between mb-6">
                            <h4 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                                <span className="p-2 bg-slate-900 text-white rounded-xl shadow-lg">{ICONS.profile}</span>
                                Strategic Next Steps
                            </h4>
                            <span className="bg-white px-3 py-1 rounded-full text-[9px] font-black text-slate-400 uppercase border border-slate-200 shadow-sm tracking-widest">ECC Roadmap</span>
                        </div>
                        
                        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-xl mb-8">
                            <h5 className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-4 flex items-center gap-2">
                                Critical Departure Check
                            </h5>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(result.logistics?.securityProcedures || []).map((proc, i) => (
                                    <li key={i} className="flex items-start gap-2 text-xs text-slate-600 leading-snug font-medium">
                                        <span className="text-blue-500 mt-1 font-bold">»</span> {proc}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        
                        <RecommendedSuitesDisplay 
                            suites={result.recommendedSuites || []} 
                            onNavigate={onNavigate} 
                            contextCountry={result.destinationCountry}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TravelSecurityBriefDisplay;
